import { BlobProvider } from "@react-pdf/renderer";
import { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import authHeader from "services/auth-header";
import API from "services/axios";
import { WOPdf } from "./WOPdf";
import Spinner from "components/Spinner";

export default function WOPdfButton({
  btnClasses,
  btnColor,
  selectedWO,
  filterBy,
  woProducts,
  // Stacking for the options dialog. The default suits callers whose parent
  // sits low (QuickView), but Bootstrap gives every modal the same z-index, so
  // opening this from a modal that has claimed a HIGHER one renders it
  // underneath and it looks like the button did nothing. Nova Shipping's
  // products modal is at 100031 and passes a value above it.
  zIndex = 100002,
  backdropClassName,
  // Fired when the PDF is generated, and again if it is downloaded.
  // QuickView uses it to flip the work order's Printed flag. Generating is
  // the trigger, not downloading: staff hit Generate Pdf and print from the
  // preview, so hooking only the Download link missed the normal path.
  // Safe to fire twice -- the handler no-ops once the flag is already set.
  onPrinted,
}) {
  /*   All States
   ********************************************* */
  const [showPdf, setShowPdf] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [locationLogo, setLocationLogo] = useState(null);
  const [pdfOptions, setPdfOptions] = useState({
    Invoice: false,
    "Show Contact & Shipping": true,
    "Show statuses": false,
  });

  const [productsListWithImages, setProductsListWithImages] = useState(null);
  const [pdfProducts, setPdfProducts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approvalColorMap, setApprovalColorMap] = useState(null);
  // Approved date for the PDF header, from the same products fetch.
  const [approvedAt, setApprovedAt] = useState(null);

  /*   All Functions
   ********************************************* */
  const getLocationLogo = async () => {
    const { data } = await API.post(
      "/design-request/get-image",
      { path: selectedWO?.location?.pdfLogo },
      {
        headers: authHeader(),
      },
    );
    setLocationLogo(data);
    return data;
  };

  const getWorkOrdersProducts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get(
        `/wo-products-with-images/${selectedWO?.id}`,
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        setApprovedAt(data?.approvedAt || null);
        let colorDetails = data?.colorDetails;
        if (colorDetails) {
          let colorMap = {}; // {id: name}
          colorDetails.forEach((color) => {
            colorMap[color.colorId] = color.name;
          });
          setApprovalColorMap(colorMap);
        }
        let updatedProductList = [];
        if (filterBy && filterBy !== "Pending") {
          if (filterBy === "READY TO BILL") {
            updatedProductList = data?.data?.filter(
              (item) => item.isReady === true,
            );
          } else {
            updatedProductList = data?.data?.filter(
              (item) => item?.productTypes?.productTypes === filterBy,
            );
          }
        } else {
          updatedProductList = data?.data;
        }
        setPdfProducts(updatedProductList);
        setProductsListWithImages(data?.data);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedWO?.id, filterBy]);

  const loadContent = async () => {
    setLoading(true);
    await getLocationLogo();
    setLoading(false);
    setShowPdf(true);
    // Generating IS the print action, per the user: they hit Generate Pdf and
    // print from the preview, so waiting for the Download link to be clicked
    // misses the common path entirely.
    onPrinted && onPrinted();
  };

  const getAvailableProductTypes = (list) => {
    if (!list) {
      return { "No Products": false };
    }

    const productTypes = list?.map((item) => item?.productTypes?.productTypes);

    let options = productTypes?.reduce((acc, cur) => {
      if (filterBy) {
        if (cur === filterBy) {
          acc[cur] = true;
        } else {
          acc[cur] = false;
        }
        return acc;
      } else {
        acc[cur] = true;
        return acc;
      }
    }, {});

    return options;
  };

  // Function to handle checkbox of productTypeList
  // And filter items according to checkbox

  const handleOptionSelect = (e, option) => {
    setShowPdf(false);
    let name = option;
    let value = e.target.checked;

    // Update pdfOptions first
    const newPdfOptions = { ...pdfOptions, [name]: value };
    setPdfOptions(newPdfOptions);

    // Skip filtering for non-product-type options
    if (
      name === "Invoice" ||
      name === "Show Contact & Shipping" ||
      name === "Show statuses"
    ) {
      return;
    }

    // Recalculate pdfProducts based on all selected product types
    const selectedProductTypes = Object.keys(newPdfOptions).filter(
      (key) =>
        key !== "Invoice" &&
        key !== "Show Contact & Shipping" &&
        newPdfOptions[key] === true,
    );

    const filteredProducts = productsListWithImages?.filter((item) =>
      selectedProductTypes.includes(item?.productTypes?.productTypes),
    );

    setPdfProducts(filteredProducts || []);
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    if (!showPdfModal) {
      return;
    }
    setShowPdf(false);
    setPdfProducts([]);
    setProductsListWithImages(null);
    setPdfOptions({ Invoice: false, "Show Contact & Shipping": true });
    setLocationLogo(null);

    let timeout = setTimeout(() => {
      getWorkOrdersProducts();
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedWO, showPdfModal, getWorkOrdersProducts]);

  useEffect(() => {
    if (productsListWithImages) {
      let optionsList = getAvailableProductTypes(productsListWithImages);

      setPdfOptions((prev) => {
        return {
          Invoice: false,
          "Show Contact & Shipping": true,
          ...optionsList,
          "Show statuses": prev["Show statuses"] ?? false,
        };
      });
    }
  }, [productsListWithImages]);

  if (!pdfOptions) return <Spinner />;

  return (
    <>
      <button
        className={`${btnClasses} ${btnColor}`}
        onClick={() => {
          setShowPdfModal(true);
        }}
        type="button"
      >
        Get Pdf
      </button>

      {/*   Delete Modal
       ********************************************* */}
      <Modal
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
        contentClassName="bg-transparent"
        style={{ zIndex }}
        backdropClassName={backdropClassName}
      >
        <Modal.Body className="custom-border-radius-sm bg-white shadow-lg px-3 py-2">
          <h3>Select Pdf Options</h3>
          <hr className="mb-3 mt-1" />
          <div className="my-3">
            {loading && <Spinner />}

            {Object.keys(pdfOptions)?.map((option) => (
              <div key={option} className="d-flex gap-2 mb-1">
                <input
                  onChange={(e) => {
                    handleOptionSelect(e, option);
                  }}
                  type="checkbox"
                  checked={pdfOptions[option]}
                  id={option}
                  data-switch="success"
                />
                <label
                  htmlFor={option}
                  data-on-label="Yes"
                  data-off-label="No"
                ></label>
                <div className="ms-2 fw-bold">
                  {option?.replaceAll("_", " ")?.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-end gap-1 mt-3">
            <button
              onClick={() => {
                setShowPdfModal(false);
              }}
              className="btn btn-secondary"
            >
              Close
            </button>
            {showPdf && locationLogo && pdfProducts && approvalColorMap ? (
              <>
                {" "}
                <BlobProvider
                  document={
                    <WOPdf
                      selectedWO={{ ...selectedWO, allApprovedAt: approvedAt }}
                      productsList={pdfProducts}
                      approvalColorMap={approvalColorMap}
                      locationLogo={locationLogo}
                      showInvoicePage={pdfOptions.Invoice}
                      showContactShipping={
                        pdfOptions["Show Contact & Shipping"]
                      }
                      showStatuses={pdfOptions["Show statuses"]}
                    />
                  }
                >
                  {({ url, blob, loading: pdfLoading, error }) => {
                    const isDisabled = (!url && !blob) || loading || pdfLoading;
                    return (
                      <div className="d-flex gap-1">
                        <a
                          href={isDisabled ? "#" : url}
                          download={`W-${selectedWO?.woId || "download"}.pdf`}
                          className="text-decoration-none text-white btn btn-success"
                          onClick={(e) => {
                            if (isDisabled) {
                              e.preventDefault();
                              return;
                            }
                            // Let the browser start the download first; the
                            // callback is a side effect and must not be able
                            // to block or cancel it.
                            onPrinted && onPrinted();
                          }}
                        >
                          <i className="mdi mdi-download"></i> Download
                        </a>
                        <a
                          href={isDisabled ? "#" : url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none text-white btn btn-primary"
                          onClick={(e) => {
                            if (isDisabled) e.preventDefault();
                          }}
                        >
                          <i className="mdi mdi-eye"></i> View
                          {(pdfLoading || !url) && (
                            <div
                              className="spinner-border spinner-border-sm text-white ms-2 mb-1"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          )}
                        </a>
                      </div>
                    );
                  }}
                </BlobProvider>
              </>
            ) : (
              <>
                <div>
                  {selectedWO?.quoteProducts?.length === 0 ? (
                    <button className={`${btnClasses} btn-danger`}>
                      No Products
                    </button>
                  ) : !selectedWO?.location ? (
                    <button className={`${btnClasses} btn-danger`}>
                      Please Select Work Order Brand First
                    </button>
                  ) : !selectedWO?.location?.pdfLogo ? (
                    <button className={`${btnClasses} btn-danger`}>
                      Please Add Brand Logo First
                    </button>
                  ) : (
                    <button
                      disabled={loading}
                      className={`btn-success btn`}
                      onClick={loadContent}
                    >
                      Generate Pdf
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
